import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../../../context/UserContextProvider";
import {
  redeemImpersonationHandoffCode,
  retrieveUserInformationFromToken,
  retrieveUserProfileListFromToken,
} from "../../../requests/ManagementRequests";
import { NotificationManager } from "../../../notifications/NotificationManager";
import i18n from "../../../language/i18n";
import { useApp } from "../../../context/AppContextProvider";
import { BaseLoadingWithText } from "../../../library";
import { loginWithExternalClientScreenStyles } from "./LoginWithExternalClientScreenStyles";
import {
  readImpersonationHandoffCode,
  retrieveAdminAppUrl,
} from "../../../utilization/ImpersonationHandoffUtilization";
import {
  beginImpersonation,
  clearImpersonationState,
} from "../../../utilization/ImpersonationSessionUtilization";

/**
 * Spends an impersonation hand-off code and signs this tab in as the target USER.
 *
 * <p>The admin chose "Öğrenci uygulaması" on admin-web's destination modal; they did NOT choose a profile.
 * The redeemed token is the pre-profile shape a password login returns (plus the `act` claim), and the
 * profile picker this screen hands over to lists the user's learner profiles. Each pick is exchanged through
 * `/authenticate/with-profile`, which keeps `act`, the session id and the 30-minute expiry.
 *
 * <p>A separate route from `/login/with-external-client`, not an extension of it: different input (a
 * single-use `#code=` versus a token pair), different output (an impersonated session with a banner), and
 * that route is a partner-facing contract worth not touching. The `/login/with-` prefix is kept on purpose
 * though — `App.tsx` pre-clears the outgoing session for anything under it, which is exactly what a redeem
 * wants, and that pre-clear WIPES the impersonation flags and this screen re-arms them immediately afterwards.
 *
 * <p>On failure the admin is sent back to admin-web rather than to app-web's login: they never had an
 * app-web session to return to, and their admin one — on a different origin — is still signed in.
 */
const LoginWithImpersonationScreen = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { setBoarding, isFullScreen, setIsFullScreen } = useApp();
  const { handleStateLogin } = useAuth();

  useEffect(() => {
    if (isFullScreen === false) {
      setIsFullScreen(true);
    }
  }, []);

  useEffect(() => {
    const bailOut = () => {
      clearImpersonationState();
      const adminUrl = retrieveAdminAppUrl();
      if (adminUrl) {
        window.location.replace(adminUrl);
        return;
      }
      navigate("/login");
    };

    const handleRedeem = async () => {
      const { code } = readImpersonationHandoffCode(hash);
      // Strip before any await. The code is single-use and about to be spent, but until it is, it is a
      // credential — and leaving it in history means a back-navigation replays a request that can only fail.
      window.history.replaceState({}, document.title, window.location.pathname);

      if (!code) {
        NotificationManager.error(i18n.t("impersonation_expired"));
        bailOut();
        return;
      }

      const { data, err } = await redeemImpersonationHandoffCode({ code });
      if (err) {
        const status = (err as any)?.response?.status;
        // 400/404/409/410 are the one indistinguishable "this code is no good" family the backend returns
        // for expired, already-spent and unknown codes alike — deliberately, so probing tells a caller
        // nothing about which it hit.
        const messageKey =
          status === 403
            ? "view_as_user_not_permitted"
            : status === 400 || status === 404 || status === 409 || status === 410
              ? "impersonation_expired"
              : "error_occurred";
        NotificationManager.error(i18n.t(messageKey));
        bailOut();
        return;
      }

      const accessToken = (data as any)?.accessToken;
      // A code minted for the ADMIN panel has no business signing in here: this picker lists learner
      // profiles only, so it would land on an empty list. Only reachable by hand-editing the hand-off URL's
      // host, but refusing costs one line.
      const destination = (data as any)?.destination;
      if (destination && destination !== "APP_WEB") {
        NotificationManager.error(i18n.t("view_as_user_failed"));
        bailOut();
        return;
      }
      if (!accessToken) {
        NotificationManager.error(i18n.t("view_as_user_failed"));
        bailOut();
        return;
      }

      const [{ data: userData, err: userErr }, { data: userProfileDTOListData }] =
        await Promise.all([
          retrieveUserInformationFromToken(accessToken),
          retrieveUserProfileListFromToken(accessToken),
        ]);
      if (userErr || !userData) {
        NotificationManager.error(i18n.t("view_as_user_failed"));
        bailOut();
        return;
      }

      // Arm the banner BEFORE the session is written, so the first post-login render already shows it.
      // There is no frame in which an impersonated session is on screen without saying so.
      //
      // Clear first, unconditionally. `beginImpersonation` refuses (returns false, writes nothing) while a
      // state is already active, and a stale-active state IS reachable here: an in-app SPA navigation to
      // this route leaves `boarding` past INITIALIZED, so App.tsx's `/login/with-` pre-clear never fires.
      // Discarding that refusal wrote the new session two lines below while the banner and the profile
      // filter still described the PREVIOUS target — exactly the "on screen without saying so" state this
      // comment claims is impossible.
      // Unconditional, and it is what GUARANTEES the arm below succeeds: `beginImpersonation`'s only
      // refusal is "already active", and this removes `impersonation.active` synchronously on the same
      // storage. A stale-active state is reachable here — an in-app SPA navigation to this route leaves
      // `boarding` past INITIALIZED, so App.tsx's `/login/with-` pre-clear never fires — which is why the
      // clear is needed. Checking the return value afterwards would be dead code, not a safety net.
      clearImpersonationState();
      beginImpersonation(
        {
          userProfileId: (data as any).targetUserProfileId,
          label: (data as any).targetLabel,
          role: (data as any).targetRole,
          impersonationSessionId: (data as any).impersonationSessionId,
          destination: (data as any).destination,
        },
        Date.now()
      );

      // An impersonated session must carry NO refresh token — that absence is the only thing bounding it to
      // 30 minutes, since nothing on this platform revokes a JWT. handleStateLogin skips a null refresh
      // token rather than clearing the key, so remove any leftover explicitly.
      localStorage.removeItem("refreshToken");
      handleStateLogin(accessToken, null, userData, userProfileDTOListData);
      setBoarding("PROFILE_SELECTION_REQUIRED");
      navigate("/");
    };
    handleRedeem();
  }, []);

  return (
    <div className="rai-root">
      <style>{loginWithExternalClientScreenStyles}</style>
      <BaseLoadingWithText
        size="large"
        primaryColor="#55777f"
        secondaryColor="55777f"
      />
    </div>
  );
};

export default LoginWithImpersonationScreen;
